import React, { useState, useEffect } from 'react';
import { PRIORITY_AR, STATUS_AR } from '../utils/labels';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fmtDateFull, fmtDateShort } from '../utils/dates';

const ACTION_AR = {
  created: 'أنشأ مهمة', status_changed: 'غيّر حالة', commented: 'علّق على',
  updated: 'عدّل', client_approved: 'اعتمد', client_revision: 'طلب مراجعة'
};

function StatCard({ value, label, icon, color, onClick, sublabel }) {
  const tones = {
    blue:   { accent: 'var(--c-blue)', tint: 'var(--c-blue-100)' },
    amber:  { accent: 'var(--c-gold)', tint: 'var(--c-gold-soft)' },
    red:    { accent: 'var(--c-coral)', tint: 'var(--c-coral-soft)' },
    green:  { accent: 'var(--c-success)', tint: 'var(--c-success-soft)' },
    violet: { accent: 'var(--c-blue-500)', tint: 'var(--c-blue-100)' },
  };
  const c = tones[color] || tones.blue;
  return (
    <div
      className="card card-hover"
      onClick={onClick}
      style={{
        padding: 'var(--s-4) var(--s-5)',
        borderRight: `4px solid ${c.accent}`,
        cursor: onClick ? 'pointer' : 'default',
        display: 'flex', alignItems: 'flex-start', gap: 'var(--s-3)',
      }}
    >
      <div style={{
        width: 40, height: 40, flexShrink: 0, borderRadius: 'var(--r-md)',
        background: c.tint, display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: 20,
      }}>{icon}</div>
      <div style={{ minWidth: 0 }}>
        <div className="num" style={{ fontSize: 30, fontWeight: 900, color: 'var(--text-strong)', lineHeight: 1.15 }}>{value}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2, fontWeight: 500, lineHeight: 1.5 }}>{label}</div>
        {sublabel && <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2, lineHeight: 1.5 }}>{sublabel}</div>}
      </div>
    </div>
  );
}

function PriorityDot({ priority }) {
  const colors = { urgent: 'var(--c-coral)', high: 'var(--c-warning)', medium: 'var(--c-blue-500)', low: 'var(--text-faint)' };
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: colors[priority] || 'var(--text-faint)', marginLeft: 5, flexShrink: 0 }} />;
}

export default function Dashboard() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [teamData, setTeamData] = useState([]);
  const [transcriptionStatus, setTranscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api('/tasks/stats/dashboard'),
      api('/reports/team-performance').catch(() => []),
      user?.role === 'admin' ? api('/settings/transcription').catch(() => null) : Promise.resolve(null)
    ]).then(([d, t, s]) => {
      setData(d);
      setTeamData(t || []);
      setTranscriptionStatus(s);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="loader">جاري التحميل...</div>;
  if (!data) return <div className="empty">تعذر تحميل البيانات</div>;

  const { stats, recentTasks, recentLogs, requestRatings } = data;

  const urgentTasks = recentTasks.filter(t => t.priority === 'urgent');
  const workload = recentTasks.reduce((acc, t) => {
    const name = t.assignee?.name || 'غير محدد';
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});
  const workloadEntries = Object.entries(workload).sort((a, b) => b[1] - a[1]).slice(0, 6);

  const today = fmtDateFull(new Date());

  return (
    <div style={{ maxWidth: 1100 }}>

      {/* Header */}
      <div className="page-header">
        <div>
          <div className="eyebrow">منصّة العمليات</div>
          <h2 style={{ margin: 0 }}>لوحة التحكم</h2>
          <div className="sub">{today}</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/tasks')}>
          📋 جميع المهام
        </button>
      </div>

      {/* Urgent Alert */}
      {urgentTasks.length > 0 && (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRight: '4px solid var(--c-coral)', borderRadius: 'var(--r-lg)',
          boxShadow: 'var(--sh-sm)', padding: 'var(--s-3) var(--s-4)', marginBottom: 'var(--s-5)',
          display: 'flex', alignItems: 'center', gap: 'var(--s-3)'
        }}>
          <span style={{ fontSize: 18 }}>🚨</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong style={{ color: 'var(--c-coral)', fontSize: 13 }}>{urgentTasks.length} مهمة عاجلة تستوجب الانتباه</strong>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.6 }}>
              {urgentTasks.map(t => t.title).join(' • ')}
            </div>
          </div>
          <button className="btn btn-coral btn-sm" onClick={() => navigate('/tasks')}>عرض</button>
        </div>
      )}

      {/* Stats Grid */}
      <div data-tour="dashboard-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        <StatCard
          value={stats.pending} label="مهام نشطة" icon="⚡" color="blue"
          onClick={() => navigate('/tasks')}
          sublabel={stats.pending > 0 ? 'انقر للعرض' : 'لا توجد مهام'}
        />
        <StatCard
          value={stats.waiting_approval} label="بانتظار اعتماد" icon="⏳" color="amber"
          onClick={() => navigate('/tasks?status=waiting_approval')}
          sublabel={stats.waiting_approval > 0 ? 'تحتاج قراراً' : 'لا يوجد معلّق'}
        />
        <StatCard
          value={stats.overdue} label="مهام متأخرة" icon="🔴" color="red"
          onClick={() => navigate('/tasks?overdue=1')}
          sublabel={stats.overdue > 0 ? 'تجاوزت الموعد' : 'كل شيء في موعده'}
        />
        <StatCard
          value={stats.completed_today} label="مكتملة اليوم" icon="✅" color="green"
          sublabel={`${stats.completed_week} مكتملة هذا الأسبوع`}
        />
      </div>

      {requestRatings && (requestRatings.count > 0 || requestRatings.awaiting > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          <StatCard
            value={requestRatings.avg ?? '—'} label="متوسط تقييم العملاء" icon="⭐" color="violet"
            onClick={() => navigate('/requests')}
            sublabel={requestRatings.count > 0 ? `من ${requestRatings.count} تقييم` : 'لا توجد تقييمات بعد'}
          />
          <div className="card" style={{ gridColumn: 'span 3', padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>تقييمات تذاكر العملاء</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {requestRatings.low > 0 && <span style={{ color: 'var(--c-danger)' }}>{requestRatings.low} تقييم منخفض • </span>}
                {requestRatings.awaiting} تذكرة بانتظار التقييم
              </span>
            </div>

            <div style={{ display: 'grid', gap: 4, marginBottom: 12 }}>
              {[5, 4, 3, 2, 1].map(star => {
                const n = requestRatings.distribution?.[star] || 0;
                const pct = requestRatings.count ? Math.round((n / requestRatings.count) * 100) : 0;
                return (
                  <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: 'var(--c-warning)', width: 44 }}>{'★'.repeat(star)}</span>
                    <div style={{ flex: 1, height: 6, background: 'var(--surface-alt)', borderRadius: 3 }}>
                      <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: star >= 4 ? 'var(--c-success)' : star === 3 ? 'var(--c-warning)' : 'var(--c-coral)' }} />
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', width: 28, textAlign: 'left' }}>{n}</span>
                  </div>
                );
              })}
            </div>

            {requestRatings.recent?.length > 0 && (
              <div style={{ display: 'grid', gap: 8 }}>
                {requestRatings.recent.map(r => (
                  <div key={r.id} style={{ fontSize: 12, color: 'var(--text-body)', borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                    <span style={{ color: 'var(--c-warning)' }}>{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                    <strong style={{ marginRight: 6 }}>{r.client?.name || '—'}</strong>
                    <span style={{ color: 'var(--text-muted)' }}> — {r.title}</span>
                    {r.rating_comment && <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{r.rating_comment}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20 }}>

        {/* Active Tasks Table */}
        <div data-tour="dashboard-recent-tasks" className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>المهام النشطة</span>
            <button className="btn btn-sm btn-outline" onClick={() => navigate('/tasks')}>عرض الكل</button>
          </div>
          {recentTasks.length === 0 ? (
            <div className="empty" style={{ padding: 40 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🎉</div>
              <div>لا توجد مهام نشطة حالياً</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--surface-alt)' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>المهمة</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>المسؤول</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>الحالة</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>الأولوية</th>
                </tr>
              </thead>
              <tbody>
                {recentTasks.map((t, i) => (
                  <tr key={t.id}
                    onClick={() => navigate(`/tasks/${t.id}`)}
                    style={{
                      cursor: 'pointer', borderTop: i > 0 ? '1px solid var(--border)' : 'none',
                      background: t.priority === 'urgent' ? 'var(--c-coral-soft)' : 'transparent',
                      transition: 'background .12s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = t.priority === 'urgent' ? 'var(--c-coral-soft)' : 'var(--surface-alt)'}
                    onMouseLeave={e => e.currentTarget.style.background = t.priority === 'urgent' ? 'var(--c-coral-soft)' : 'transparent'}
                  >
                    <td style={{ padding: '11px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <PriorityDot priority={t.priority} />
                        <span style={{ fontWeight: 500, fontSize: 13, color: 'var(--text)' }}>
                          {t.parent && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{t.parent.title} ← </span>}
                          {t.title}
                        </span>
                      </div>
                      {t.client?.name && <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2, paddingRight: 13 }}>{t.client.name}</div>}
                    </td>
                    <td style={{ padding: '11px 12px', fontSize: 13, color: 'var(--text-body)' }}>{t.assignee?.name || '—'}</td>
                    <td style={{ padding: '11px 12px' }}>
                      <span className={`badge badge-${t.status}`} style={{ fontSize: 11 }}>{STATUS_AR[t.status]}</span>
                    </td>
                    <td style={{ padding: '11px 12px' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 500, padding: '2px 7px', borderRadius: 20,
                        background: t.priority === 'urgent' ? 'var(--c-coral-soft)' : t.priority === 'high' ? 'var(--c-gold-soft)' : t.priority === 'medium' ? 'var(--c-blue-100)' : 'var(--surface-alt)',
                        color: t.priority === 'urgent' ? 'var(--c-danger)' : t.priority === 'high' ? 'var(--c-gold-ink)' : t.priority === 'medium' ? 'var(--text-strong)' : 'var(--text-muted)'
                      }}>
                        {PRIORITY_AR[t.priority] || t.priority}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Transcription Service Status (admin only) */}
          {user?.role === 'admin' && transcriptionStatus && (
            <div className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>🎙️</span>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>خدمة التفريغ</span>
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20,
                  background: transcriptionStatus.configured ? 'var(--c-success-soft)' : 'var(--c-gold-soft)',
                  color: transcriptionStatus.configured ? 'var(--c-success)' : 'var(--c-gold-ink)'
                }}>
                  {transcriptionStatus.configured ? '✓ متصل' : '⚠ غير مضبوط'}
                </span>
              </div>
              {transcriptionStatus.masked_key && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace', marginBottom: 10 }}>
                  {transcriptionStatus.masked_key}
                </div>
              )}
              <button className="btn btn-sm btn-outline" style={{ width: '100%' }}
                onClick={() => navigate('/settings')}>
                {transcriptionStatus.configured ? 'إدارة الإعدادات' : 'ضبط المفتاح'}
              </button>
              {transcriptionStatus.masked_key && (
                <a
                  href={transcriptionStatus.masked_key.startsWith('fw_')
                    ? 'https://fireworks.ai/account/billing'
                    : 'https://console.groq.com/settings/billing'}
                  target="_blank" rel="noreferrer"
                  className="btn btn-sm btn-outline"
                  style={{ width: '100%', display: 'block', textAlign: 'center', marginTop: 8, textDecoration: 'none' }}
                  title="يفتح صفحة الفوترة لدى المزوّد لعرض الرصيد المتبقّي"
                >
                  💳 رصيد {transcriptionStatus.masked_key.startsWith('fw_') ? 'Fireworks' : 'Groq'} المتبقّي ↗
                </a>
              )}

            </div>
          )}

          {/* Team Workload */}
          {workloadEntries.length > 0 && (
            <div className="card">
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14, color: 'var(--text)' }}>عبء العمل</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {workloadEntries.map(([name, count]) => {
                  const memberData = Array.isArray(teamData) ? teamData.find(m => m.name === name) : null;
                  const maxCount = workloadEntries[0][1];
                  const pct = Math.round((count / maxCount) * 100);
                  return (
                    <div key={name}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{name}</span>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {count} نشطة {memberData ? `• ${memberData.completed} مكتملة` : ''}
                        </span>
                      </div>
                      <div style={{ height: 6, background: 'var(--surface-alt)', borderRadius: 3 }}>
                        <div style={{
                          width: `${pct}%`, height: '100%', borderRadius: 3,
                          background: pct > 70 ? 'var(--c-coral)' : pct > 40 ? 'var(--c-warning)' : 'var(--c-blue-500)',
                          transition: 'width .4s'
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Activity Log */}
          <div data-tour="dashboard-recent-logs" className="card" style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14, color: 'var(--text)' }}>آخر النشاطات</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {recentLogs.slice(0, 10).map((l, i) => (
                <div key={l.id} style={{
                  display: 'flex', gap: 10, paddingTop: i > 0 ? 10 : 0,
                  marginTop: i > 0 ? 10 : 0,
                  borderTop: i > 0 ? '1px solid var(--border)' : 'none'
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', background: 'var(--c-blue-100)',
                    color: 'var(--c-blue)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700, flexShrink: 0
                  }}>
                    {l.user?.name?.charAt(0) || '؟'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: 'var(--text-body)', lineHeight: 1.4 }}>
                      <strong>{l.user?.name}</strong> {ACTION_AR[l.action] || l.action}
                      {l.task?.title && <span style={{ color: 'var(--text-muted)' }}> — {l.task.title}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}>
                      {fmtDateShort(l.created_at)} {new Date(l.created_at).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              {recentLogs.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-faint)', textAlign: 'center', padding: '20px 0' }}>لا توجد نشاطات بعد</div>}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
